import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2, File as FileIcon, Loader, TriangleAlert, Trash2, Upload,
} from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserIdQueryKey,
  getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeesQueryKey,
  getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdLatestQueryKey,
  usePostApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserIdFile,
  usePostApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdFile,
} from '@/api/generated/company-management-user/company-management-user'
import type { PersonalDocumentEmployeeResponse } from '@/api/model'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { formatFileSize } from './utils'

interface Props {
  serviceModuleId: number
  /** Target employee on direct upload; without = global upload (assignment by personnel number). */
  employee: PersonalDocumentEmployeeResponse | null
  initialFiles: File[]
  onClose: () => void
}

type UploadStatus = 'pending' | 'uploading' | 'success' | 'error'
interface FileWithStatus {
  file: File
  status: UploadStatus
  error?: string
}

export function UploadDocumentsDialog({ serviceModuleId, employee, initialFiles, onClose }: Props) {
  const { t } = useTranslation(['service-modules-personal-documents', 'common'])
  const queryClient = useQueryClient()
  const [files, setFiles] = useState<FileWithStatus[]>(() =>
    initialFiles.map((file) => ({ file, status: 'pending' as const })),
  )
  const [isUploading, setIsUploading] = useState(false)
  // „Abbrechen" stops the queue between files — the generated mutation hooks
  // don't pass an AbortSignal through, so the file in flight finishes.
  const stopRef = useRef(false)

  const uploadGlobal = usePostApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdFile()
  const uploadForEmployee =
    usePostApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserIdFile()

  const employeeId = employee?.employeeId ?? null

  function invalidate() {
    return Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeesQueryKey(serviceModuleId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdLatestQueryKey(serviceModuleId),
      }),
      employeeId
        ? queryClient.invalidateQueries({
            queryKey: getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserIdQueryKey(serviceModuleId, employeeId),
          })
        : Promise.resolve(),
    ])
  }

  function setStatus(index: number, status: UploadStatus, error?: string) {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, status, error } : f)))
  }

  async function uploadOne(index: number, file: File) {
    setStatus(index, 'uploading')
    try {
      if (employeeId) {
        await uploadForEmployee.mutateAsync({ serviceModuleId, employeeUserId: employeeId, data: { File: file } })
      } else {
        await uploadGlobal.mutateAsync({ serviceModuleId, data: { File: file } })
      }
      setStatus(index, 'success')
    } catch (error) {
      setStatus(index, 'error', (error as any)?.response?.data?.message ?? t('personalDocuments.uploadError'))
    }
  }

  async function handleUploadAll() {
    setIsUploading(true)
    stopRef.current = false
    // read the current snapshot: skip successes, stop on „Abbrechen"
    const snapshot = files
    for (let i = 0; i < snapshot.length; i++) {
      if (stopRef.current) break
      if (snapshot[i].status === 'success') continue
      await uploadOne(i, snapshot[i].file)
    }
    await invalidate()
    setIsUploading(false)
  }

  async function handleRetry(index: number) {
    setIsUploading(true)
    await uploadOne(index, files[index].file)
    await invalidate()
    setIsUploading(false)
  }

  function handleRemove(index: number) {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index)
      if (next.length === 0) onClose()
      return next
    })
  }

  const allDone = files.length > 0 && files.every((f) => f.status === 'success')

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !isUploading) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {employee
              ? `${t('personalDocuments.uploadDialogTitleEmployee')} ${employee.firstName} ${employee.lastName}`
              : t('personalDocuments.uploadDialogTitleGeneral')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">
          {t('personalDocuments.filesCount')}: {files.length}
        </p>

        <ul className="flex max-h-80 flex-col divide-y overflow-y-auto rounded-md border">
          {files.map((item, index) => (
            <li key={`${item.file.name}-${index}`} className="flex items-center gap-3 px-3 py-2">
              {statusIcon(item.status)}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{item.file.name}</p>
                <p className={`text-xs ${item.status === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {item.error ?? formatFileSize(item.file.size)}
                </p>
              </div>
              {(item.status === 'pending' || item.status === 'error') && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="icon" className="size-7"
                    aria-label={t('buttons.retry', { ns: 'common' })} disabled={isUploading}
                    onClick={() => handleRetry(index)}
                  >
                    <Upload className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="icon" className="size-7"
                    aria-label={t('personalDocuments.remove')} disabled={isUploading}
                    onClick={() => handleRemove(index)}
                  >
                    <Trash2 className="size-3.5 text-destructive" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUploading}>
            {t('buttons.close', { ns: 'common' })}
          </Button>
          {isUploading ? (
            <Button variant="destructive" onClick={() => { stopRef.current = true }}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
          ) : (
            <Button onClick={handleUploadAll} disabled={files.length === 0 || allDone}>
              <Upload className="size-4" />
              {t('personalDocuments.upload')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Status-dependent icon of a file row (render helper, not its own component). */
function statusIcon(status: UploadStatus) {
  if (status === 'uploading') return <Loader className="size-5 shrink-0 animate-spin text-primary" />
  if (status === 'success') return <CheckCircle2 className="size-5 shrink-0 text-emerald-600" />
  if (status === 'error') return <TriangleAlert className="size-5 shrink-0 text-destructive" />
  return <FileIcon className="size-5 shrink-0 text-muted-foreground" />
}
